import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FaPlus, FaEdit, FaTrash, FaCopy, FaSave, FaTimes, FaSearch,
  FaLock, FaDumbbell, FaLayerGroup, FaMagic
} from 'react-icons/fa';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MUSCLE_OPTIONS = [
  'pecho', 'espalda', 'hombros', 'biceps', 'triceps', 'antebrazos',
  'gluteo', 'cuadriceps', 'isquios', 'aductor', 'abductor', 'pantorrilla', 'lumbar', 'core',
];

const BLOCK_FIELDS = [
  { key: 'activador_count', label: 'Activador', color: '#f59e0b' },
  { key: 'principal_count', label: 'Principal', color: '#ef4444' },
  { key: 'secundario_count', label: 'Secundario', color: '#3b82f6' },
  { key: 'terciario_count', label: 'Terciario', color: '#8b5cf6' },
  { key: 'complementario_count', label: 'Complementario', color: '#10b981' },
];

const emptyDay = (n) => ({
  day_number: n,
  day_name: `Día ${n}`,
  focus: '',
  day_type: '',
  muscle_order: [],
  activador_count: 1,
  principal_count: 2,
  secundario_count: 1,
  terciario_count: 0,
  complementario_count: 1,
  // 2026-02 · Día de descanso. Si está marcado, el generador no asigna
  // ejercicios y en la vista del plan aparece claramente diferenciado.
  is_rest_day: false,
  rest_notes: '',
});

const emptyTemplate = () => ({
  name: '',
  description: '',
  days_count: 3,
  days: [emptyDay(1), emptyDay(2), emptyDay(3)],
  allowed_techniques: [],
  gender: 'ambos',
  difficulty_level: '',
  is_generic: false,
});

const GENDER_OPTIONS = [
  { value: 'ambos', label: 'Ambos', icon: '⚧', color: '#a855f7' },
  { value: 'femenino', label: 'Femenino', icon: '♀', color: '#ec4899' },
  { value: 'masculino', label: 'Masculino', icon: '♂', color: '#3b82f6' },
];

const DIFFICULTY_OPTIONS = [
  { value: '', label: '— Sin especificar —' },
  { value: 'novato', label: 'Novato' },
  { value: 'intermedio', label: 'Intermedio' },
  { value: 'avanzado', label: 'Avanzado' },
];

const DAY_TYPE_OPTIONS = [
  { value: '', label: '— Rol tradicional —' },
  { value: 'full_body', label: 'Full Body (todos los músculos)' },
  { value: 'full_upper', label: 'Full Upper (tren superior)' },
  { value: 'full_legs', label: 'Full Legs (tren inferior)' },
];

export default function FitEvolutionTemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('ambos'); // 'ambos' = sin filtro
  const [editing, setEditing] = useState(null); // { mode: 'create'|'edit', data, id? }
  const [saving, setSaving] = useState(false);
  const [methods, setMethods] = useState([]); // [{slug, name}]

  useEffect(() => {
    fetchAll();
  }, [genderFilter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (genderFilter && genderFilter !== 'ambos') params.gender = genderFilter;
      const [tplsRes, methodsRes] = await Promise.all([
        axios.get(`${API}/admin/fit-evolution/templates`, { params }),
        axios.get(`${API}/admin/offline-generator/training-methods`),
      ]);
      setTemplates(tplsRes.data.templates || []);
      const m = methodsRes.data || {};
      setMethods(Object.entries(m).map(([slug, v]) => ({ slug, name: v.name })));
    } catch (e) {
      toast.error('Error cargando plantillas: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  }, [templates, search]);

  const openCreate = () => setEditing({ mode: 'create', data: emptyTemplate() });
  const openEdit = (t) => {
    setEditing({ mode: 'edit', id: t.id, data: JSON.parse(JSON.stringify(t)) });
  };

  const duplicate = async (t) => {
    if (!window.confirm(`¿Duplicar la plantilla "${t.name}"?`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/fit-evolution/templates/${t.id}/duplicate`);
      toast.success('Plantilla duplicada');
      await fetchAll();
      if (data?.template) openEdit(data.template);
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar la plantilla "${t.name}"? Esta acción es irreversible.`)) return;
    try {
      await axios.delete(`${API}/admin/fit-evolution/templates/${t.id}`);
      toast.success('Plantilla eliminada');
      await fetchAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const save = async () => {
    if (!editing) return;
    const d = editing.data;
    if (!d.name?.trim()) { toast.error('Ponle un nombre a la plantilla'); return; }
    if (!Array.isArray(d.days) || d.days.length !== d.days_count) {
      toast.error(`Debe haber exactamente ${d.days_count} días configurados`); return;
    }
    for (const day of d.days) {
      if (!day.day_name?.trim()) { toast.error(`Día ${day.day_number}: el nombre no puede estar vacío`); return; }
      // Días de descanso no requieren músculos
      if (!day.is_rest_day && (day.muscle_order || []).length === 0) {
        toast.error(`Día ${day.day_number}: selecciona al menos 1 músculo (o márcalo como día de descanso)`); return;
      }
    }
    setSaving(true);
    try {
      // No enviar is_generic al crear (default false) ni al editar (backend lo ignora)
      const payload = {
        name: d.name.trim(),
        description: d.description || '',
        days_count: d.days_count,
        days: d.days.map(day => ({
          day_number: day.day_number,
          day_name: day.day_name.trim(),
          focus: day.focus || '',
          day_type: (day.day_type || '') || null,
          muscle_order: day.is_rest_day ? [] : (day.muscle_order || []),
          activador_count: day.is_rest_day ? 0 : (Number(day.activador_count) || 0),
          principal_count: day.is_rest_day ? 0 : (Number(day.principal_count) || 0),
          secundario_count: day.is_rest_day ? 0 : (Number(day.secundario_count) || 0),
          terciario_count: day.is_rest_day ? 0 : (Number(day.terciario_count) || 0),
          complementario_count: day.is_rest_day ? 0 : (Number(day.complementario_count) || 0),
          is_rest_day: !!day.is_rest_day,
          rest_notes: day.is_rest_day ? (day.rest_notes || '') : '',
        })),
        allowed_techniques: d.allowed_techniques || [],
        gender: d.gender || 'ambos',
        difficulty_level: (d.difficulty_level || '') || null,
        is_generic: false,
      };
      if (editing.mode === 'create') {
        await axios.post(`${API}/admin/fit-evolution/templates`, payload);
        toast.success('Plantilla creada');
      } else {
        await axios.put(`${API}/admin/fit-evolution/templates/${editing.id}`, payload);
        toast.success('Plantilla actualizada');
      }
      setEditing(null);
      await fetchAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const seedGeneric = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/fit-evolution/templates/seed-generic`);
      toast.success(`Genéricas cargadas: +${data.inserted} (total ${data.total_generic})`);
      await fetchAll();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ─── Editor helpers ─────────────────────────────────────────
  const updateDaysCount = (n) => {
    if (!editing) return;
    n = Math.max(1, Math.min(7, Number(n) || 1));
    const days = [...(editing.data.days || [])];
    while (days.length < n) days.push(emptyDay(days.length + 1));
    while (days.length > n) days.pop();
    days.forEach((d, i) => { d.day_number = i + 1; });
    setEditing({ ...editing, data: { ...editing.data, days_count: n, days } });
  };

  const updateDay = (idx, patch) => {
    const days = [...editing.data.days];
    days[idx] = { ...days[idx], ...patch };
    setEditing({ ...editing, data: { ...editing.data, days } });
  };

  const toggleMuscle = (idx, muscle) => {
    const day = editing.data.days[idx];
    const list = day.muscle_order || [];
    const next = list.includes(muscle) ? list.filter(m => m !== muscle) : [...list, muscle];
    updateDay(idx, { muscle_order: next });
  };

  const toggleTechnique = (slug) => {
    const list = editing.data.allowed_techniques || [];
    const next = list.includes(slug) ? list.filter(s => s !== slug) : [...list, slug];
    setEditing({ ...editing, data: { ...editing.data, allowed_techniques: next } });
  };

  // ─── UI ─────────────────────────────────────────────────────
  if (editing) return renderEditor();

  return (
    <div className="space-y-6" data-testid="fe-template-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FaLayerGroup className="text-fuchsia-400" /> Plantillas de Entrenamiento
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Define bases reutilizables (días, músculos, bloques y técnicas) para generar rutinas de Fit Evolution.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={seedGeneric}
            variant="outline"
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
            data-testid="fe-templates-seed-generic-btn"
          >
            <FaMagic className="mr-2" /> Cargar genéricas
          </Button>
          <Button
            onClick={openCreate}
            className="bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white"
            data-testid="fe-templates-create-btn"
          >
            <FaPlus className="mr-2" /> Nueva plantilla
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o descripción…"
            className="pl-9 bg-slate-900/60 border-white/10 text-white"
            data-testid="fe-templates-search"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="fe-templates-gender-filter">
          <span className="text-xs text-slate-400 mr-1">Género:</span>
          {GENDER_OPTIONS.map(g => {
            const active = genderFilter === g.value;
            return (
              <button
                key={g.value}
                type="button"
                onClick={() => setGenderFilter(g.value)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                  active
                    ? 'text-white'
                    : 'bg-slate-800/60 border-white/10 text-slate-300 hover:border-white/30'
                }`}
                style={active ? { background: `${g.color}25`, borderColor: `${g.color}80` } : {}}
                data-testid={`fe-templates-gender-filter-${g.value}`}
              >
                {g.icon} {g.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <p className="text-center text-slate-400 py-12">Cargando plantillas…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-white/10 rounded-xl">
          <p className="text-slate-400 mb-3">No hay plantillas todavía.</p>
          <Button onClick={openCreate} className="bg-fuchsia-600 text-white">
            <FaPlus className="mr-2" /> Crear la primera
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="fe-templates-grid">
          {filtered.map(t => (
            <div
              key={t.id}
              className="rounded-xl p-4 border transition-all hover:border-fuchsia-400/60"
              style={{
                background: 'linear-gradient(180deg, rgba(30,27,75,0.55), rgba(15,23,42,0.85))',
                borderColor: t.is_generic ? 'rgba(6,182,212,0.35)' : 'rgba(255,255,255,0.08)',
              }}
              data-testid={`fe-template-card-${t.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <h3 className="text-white font-semibold truncate">{t.name}</h3>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {t.is_generic && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 flex items-center gap-1">
                        <FaLock className="text-[8px]" /> Genérica
                      </span>
                    )}
                    {(() => {
                      const g = GENDER_OPTIONS.find(x => x.value === (t.gender || 'ambos')) || GENDER_OPTIONS[0];
                      return (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded border"
                          style={{ background: `${g.color}22`, color: g.color, borderColor: `${g.color}55` }}
                          data-testid={`fe-template-gender-badge-${t.id}`}
                        >
                          {g.icon} {g.label}
                        </span>
                      );
                    })()}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300">
                      📅 {t.days_count} días
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300">
                      🎯 {(t.allowed_techniques || []).length} técnicas
                    </span>
                  </div>
                </div>
              </div>
              {t.description && (
                <p className="text-xs text-slate-300 mb-3 line-clamp-2">{t.description}</p>
              )}
              <div className="flex flex-wrap gap-1 mb-3">
                {(t.days || []).map((d, i) => (
                  <span
                    key={i}
                    className={`text-[10px] px-2 py-0.5 rounded border ${
                      d.is_rest_day
                        ? 'bg-slate-600/40 border-slate-400/40 text-slate-200 italic'
                        : 'bg-slate-800/70 border-white/5 text-slate-300'
                    }`}
                    title={d.is_rest_day
                      ? `Descanso${d.rest_notes ? ` · ${d.rest_notes}` : ''}`
                      : (d.muscle_order || []).join(', ')}
                    data-testid={`fe-template-day-badge-${t.id}-${i}`}
                  >
                    {d.is_rest_day ? `🌙 ${d.day_name}` : d.day_name}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
                <Button
                  size="sm"
                  onClick={() => openEdit(t)}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5"
                  data-testid={`fe-template-edit-${t.id}`}
                >
                  <FaEdit className="mr-1.5" /> Editar
                </Button>
                <Button
                  size="sm"
                  onClick={() => duplicate(t)}
                  variant="outline"
                  className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
                  data-testid={`fe-template-duplicate-${t.id}`}
                >
                  <FaCopy className="mr-1.5" /> Duplicar
                </Button>
                <Button
                  size="sm"
                  onClick={() => remove(t)}
                  variant="outline"
                  className="border-red-500/30 text-red-300 hover:bg-red-500/10 ml-auto"
                  data-testid={`fe-template-delete-${t.id}`}
                >
                  <FaTrash />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ─── Editor render ──────────────────────────────────────────
  function renderEditor() {
    const d = editing.data;
    return (
      <div className="space-y-5" data-testid="fe-template-editor">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <FaDumbbell className="text-fuchsia-400" />
              {editing.mode === 'create' ? 'Nueva plantilla' : 'Editar plantilla'}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Define nombre, días, músculos por día, ejercicios por bloque y técnicas permitidas.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setEditing(null)}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5"
              data-testid="fe-template-editor-cancel"
            >
              <FaTimes className="mr-2" /> Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white"
              data-testid="fe-template-editor-save"
            >
              <FaSave className="mr-2" /> {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </div>

        <div
          className="rounded-xl p-4 border border-white/10 space-y-4"
          style={{ background: 'rgba(15,23,42,0.6)' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-slate-300">Nombre</Label>
              <Input
                value={d.name}
                onChange={(e) => setEditing({ ...editing, data: { ...d, name: e.target.value } })}
                placeholder="Ej: Push/Pull/Legs (3 días)"
                className="bg-slate-900/60 border-white/10 text-white mt-1"
                data-testid="fe-template-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Cantidad de días</Label>
              <Input
                type="number"
                min={1}
                max={7}
                value={d.days_count}
                onChange={(e) => updateDaysCount(e.target.value)}
                className="bg-slate-900/60 border-white/10 text-white mt-1"
                data-testid="fe-template-days-count-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Dirigida a</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="fe-template-gender-input">
              {GENDER_OPTIONS.map(g => {
                const active = (d.gender || 'ambos') === g.value;
                return (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setEditing({ ...editing, data: { ...d, gender: g.value } })}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-all ${
                      active ? 'text-white' : 'bg-slate-800/60 border-white/10 text-slate-300 hover:border-white/30'
                    }`}
                    style={active ? { background: `${g.color}25`, borderColor: `${g.color}80` } : {}}
                    data-testid={`fe-template-gender-option-${g.value}`}
                  >
                    {g.icon} {g.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              El filtro del listado mostrará esta plantilla para el género seleccionado (o siempre si es &quot;Ambos&quot;).
            </p>
          </div>
          <div>
            <Label className="text-slate-300">Nivel de dificultad (opcional)</Label>
            <select
              value={d.difficulty_level || ''}
              onChange={(e) => setEditing({ ...editing, data: { ...d, difficulty_level: e.target.value } })}
              className="w-full mt-1 px-3 py-2 rounded-md bg-slate-900/60 border border-white/10 text-white text-sm"
              data-testid="fe-template-difficulty-input"
            >
              {DIFFICULTY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-slate-300">Descripción</Label>
            <Textarea
              value={d.description}
              onChange={(e) => setEditing({ ...editing, data: { ...d, description: e.target.value } })}
              placeholder="Describe brevemente para qué sirve esta plantilla…"
              rows={2}
              className="bg-slate-900/60 border-white/10 text-white mt-1 whitespace-pre-line"
              data-testid="fe-template-description-input"
            />
          </div>
        </div>

        {/* Days */}
        <div className="space-y-3">
          {d.days.map((day, idx) => (
            <div
              key={idx}
              className="rounded-xl p-4 border"
              style={day.is_rest_day ? {
                background: 'linear-gradient(180deg, rgba(71,85,105,0.35), rgba(15,23,42,0.85))',
                borderColor: 'rgba(148,163,184,0.35)',
                borderStyle: 'dashed',
              } : {
                background: 'linear-gradient(180deg, rgba(30,27,75,0.5), rgba(15,23,42,0.85))',
                borderColor: 'rgba(255,255,255,0.1)',
              }}
              data-testid={`fe-template-day-${day.day_number}`}
            >
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span
                  className={`w-8 h-8 rounded-full border flex items-center justify-center font-bold text-sm ${
                    day.is_rest_day
                      ? 'bg-slate-500/20 border-slate-400/40 text-slate-200'
                      : 'bg-fuchsia-500/20 border-fuchsia-400/40 text-fuchsia-200'
                  }`}
                >
                  {day.is_rest_day ? '😴' : day.day_number}
                </span>
                <Input
                  value={day.day_name}
                  onChange={(e) => updateDay(idx, { day_name: e.target.value })}
                  placeholder={`Día ${day.day_number}`}
                  className="bg-slate-900/60 border-white/10 text-white flex-1 max-w-xs"
                  data-testid={`fe-template-day-name-${day.day_number}`}
                />
                {!day.is_rest_day && (
                  <>
                    <Input
                      value={day.focus}
                      onChange={(e) => updateDay(idx, { focus: e.target.value })}
                      placeholder="Foco (opcional): Empuje, Jalón, Pierna…"
                      className="bg-slate-900/60 border-white/10 text-white flex-1"
                      data-testid={`fe-template-day-focus-${day.day_number}`}
                    />
                    <select
                      value={day.day_type || ''}
                      onChange={(e) => updateDay(idx, { day_type: e.target.value })}
                      className="px-2 py-2 rounded-md bg-slate-900/60 border border-white/10 text-white text-xs max-w-[210px]"
                      title="Si seleccionas full body/upper/legs, el generador ignora principal/secundario/terciario/complementario y elige un ejercicio principal por cada músculo del grupo."
                      data-testid={`fe-template-day-type-${day.day_number}`}
                    >
                      {DAY_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </>
                )}
                <label
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border cursor-pointer transition-all select-none ${
                    day.is_rest_day
                      ? 'bg-slate-500/25 border-slate-400/60 text-slate-100'
                      : 'bg-slate-800/60 border-white/10 text-slate-300 hover:border-slate-400/50'
                  }`}
                  data-testid={`fe-template-rest-toggle-${day.day_number}`}
                  title="Marcar este día como día de descanso. El generador no asignará ejercicios."
                >
                  <input
                    type="checkbox"
                    checked={!!day.is_rest_day}
                    onChange={(e) => updateDay(idx, { is_rest_day: e.target.checked })}
                    className="accent-slate-400 w-3.5 h-3.5"
                    data-testid={`fe-template-rest-checkbox-${day.day_number}`}
                  />
                  🌙 Día de descanso
                </label>
              </div>

              {day.is_rest_day ? (
                <div className="space-y-2">
                  <Label className="text-slate-300 text-xs flex items-center gap-1.5">
                    📝 Notas / indicaciones del día (opcional)
                  </Label>
                  <Textarea
                    value={day.rest_notes || ''}
                    onChange={(e) => updateDay(idx, { rest_notes: e.target.value })}
                    placeholder="Ej: descanso activo, estiramiento 15 min, caminata ligera, movilidad, foam roller…"
                    rows={2}
                    className="bg-slate-900/60 border-white/10 text-white whitespace-pre-line"
                    data-testid={`fe-template-rest-notes-${day.day_number}`}
                  />
                  <p className="text-[11px] text-slate-500 italic">
                    💤 El generador NO asignará ejercicios este día. Las notas se mostrarán a la clienta.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <p className="text-xs text-slate-400 mb-1.5">Músculos del día (orden = prioridad)</p>
                    <div className="flex flex-wrap gap-1.5">
                      {MUSCLE_OPTIONS.map(m => {
                        const active = (day.muscle_order || []).includes(m);
                        return (
                          <button
                            key={m}
                            type="button"
                            onClick={() => toggleMuscle(idx, m)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-all capitalize ${
                              active
                                ? 'bg-fuchsia-500/25 border-fuchsia-400/60 text-white'
                                : 'bg-slate-800/60 border-white/10 text-slate-300 hover:border-fuchsia-400/40'
                            }`}
                            data-testid={`fe-template-muscle-${day.day_number}-${m}`}
                          >
                            {m}
                          </button>
                        );
                      })}
                    </div>
                    {(day.muscle_order || []).length > 0 && (
                      <p className="text-[11px] text-slate-500 mt-1.5">
                        Orden: {(day.muscle_order || []).join(' → ')}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {BLOCK_FIELDS.map(f => (
                      <div key={f.key}>
                        <Label className="text-[11px] uppercase tracking-wide" style={{ color: f.color }}>
                          {f.label}
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          max={6}
                          value={day[f.key]}
                          onChange={(e) => updateDay(idx, { [f.key]: Number(e.target.value) || 0 })}
                          className="bg-slate-900/60 border-white/10 text-white mt-1"
                          data-testid={`fe-template-block-${day.day_number}-${f.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Allowed techniques */}
        <div
          className="rounded-xl p-4 border border-white/10"
          style={{ background: 'rgba(15,23,42,0.6)' }}
        >
          <p className="text-sm text-slate-300 mb-2 font-medium">Técnicas permitidas</p>
          <p className="text-xs text-slate-500 mb-3">
            Al aplicar esta plantilla, el generador podrá asignar únicamente estas técnicas.
          </p>
          {methods.length === 0 ? (
            <p className="text-xs text-slate-500 italic">
              No se cargaron técnicas. Verifica el gestor de entrenamiento.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {methods.map(m => {
                const active = (d.allowed_techniques || []).includes(m.slug);
                return (
                  <button
                    key={m.slug}
                    type="button"
                    onClick={() => toggleTechnique(m.slug)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                      active
                        ? 'bg-cyan-500/25 border-cyan-400/60 text-white'
                        : 'bg-slate-800/60 border-white/10 text-slate-300 hover:border-cyan-400/40'
                    }`}
                    data-testid={`fe-template-technique-${m.slug}`}
                  >
                    {m.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
}
